import type { SubIBStats } from "../types";

import { InfoIcon } from "../../Icons";
import { SkeletonLoader } from "../../SkeletonLoader";
import { TooltipWrapper } from "../../Tooltip";
import { Typo } from "../../Typo";

type SubIBStatCardsProps = {
  stats: SubIBStats | undefined;
  isLoading: boolean;
  isError: boolean;
};

const fmt = (n: number): string =>
  Math.round(n).toLocaleString("en-US", { maximumFractionDigits: 0 });

// Matches the confirmed design's table-states spec: stats show "—" on error,
// sharing the same error signal as the table.
const CardValue = ({
  isLoading,
  isError,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  children: React.ReactNode;
}) =>
  isError ? (
    <Typo element="span" type="headingXL" textColor="text-text-disabled">
      —
    </Typo>
  ) : isLoading ? (
    <SkeletonLoader height={32} width={80} variant="rounded" />
  ) : (
    <>{children}</>
  );

export const SubIBStatCards = ({
  stats,
  isLoading,
  isError,
}: SubIBStatCardsProps) => (
  <div className="-mx-1 overflow-x-auto px-1" data-testid="sub-ib-stat-cards">
    <div
      className="grid auto-cols-fr grid-flow-col gap-6"
      style={{ minWidth: "320px" }}
    >
      {/* Active */}
      <div className="flex min-h-[123px] flex-col justify-center gap-4 rounded-2xl bg-surface-brand-subtle p-6 font-sans">
        <Typo element="span" type="labelSM" textColor="text-text-secondary">
          Active
        </Typo>
        <CardValue isLoading={isLoading} isError={isError}>
          <Typo element="span" type="headingXL" textColor="text-text-primary">
            {fmt(stats?.activeSubIbs ?? 0)}
          </Typo>
        </CardValue>
      </div>

      {/* Shared payout */}
      <div className="flex min-h-[123px] flex-col justify-center gap-4 rounded-2xl bg-surface-brand-subtle p-6 font-sans">
        <span className="flex items-center gap-1.5">
          <Typo
            element="span"
            type="labelSM"
            textColor="text-text-secondary"
            className="whitespace-nowrap"
          >
            Shared payout
          </Typo>
          <TooltipWrapper
            cursor="help"
            text="Combined Sub IB share across all programs for the selected period. Per-partner figures — no double-counting."
            color="blue"
            tooltipMaxWidth={240}
            tooltipContainerClassName="!text-xs"
          >
            <InfoIcon className="h-3.5 w-3.5 shrink-0 text-text-secondary" />
          </TooltipWrapper>
        </span>
        <CardValue isLoading={isLoading} isError={isError}>
          <Typo element="span" type="headingXL" textColor="text-text-primary">
            {fmt(stats?.sharedPayout ?? 0)}
            <Typo
              element="span"
              type="labelSM"
              textColor="text-text-secondary"
              className="ml-1"
            >
              USD
            </Typo>
          </Typo>
        </CardValue>
      </div>
    </div>
  </div>
);
